import React, { useEffect, useRef, useState } from 'react';
import maplibregl, { LngLat, LngLatBounds } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useWindowSize } from '@utils/resize';
import instance from 'api/instance';
import { Tramo } from '@componentes/Tabla/types';
import useToggle from '@utils/useToggle';
import { addClusterCountLayer, addClusterLayer, addGeoJsonSource, addUnclusteredPointLayer, mapClick, mapClickCluster, mouseEnter, mouseEnterMarker } from '@utils/mapFunctions';
import Leyenda from '@componentes/Leyenda/Leyenda';
import ReactDOMServer from 'react-dom/server';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { obtenerEstadoTrafico } from '@utils/estadoTrafico';
import './map.css'
interface MapaProps {
  coordinates?: { lng: number, lat: number, forzadura?: number, nivel?: number, id: number, nombre?: string, isPanel?: boolean, description?: string, image?: string, fechaInicio?: string, fechaFin?: string, direccion?: string, plazasLibres?: string, plazasTotales?: string, estado?: number, desc_estado?: string }[];
  coordinatesToGo?: { lng: number, lat: number, id: number };
  height?: number;
  mapWidth?: string;
  setMarked?: (id: string) => void;
  info?: Tramo[]
  isPrincipal?: boolean
}

const Mapa: React.FC<MapaProps> = ({ coordinates, coordinatesToGo, height, mapWidth, setMarked, info, isPrincipal }) => {
  const { width } = useWindowSize();
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersPanels = useRef<maplibregl.Marker[]>([]);
  const markersSenals = useRef<maplibregl.Marker[]>([]);
  const [configLoaded, setConfigLoaded] = useToggle(false);
  const [apiUrlConfig, setApiUrlConfig] = useState('');
  const [lng, setLng] = useState<number | null>(null);
  const [lat, setLat] = useState<number | null>(null);
  const [sw, setSw] = useState<LngLat | null>(null);
  const [ne, setNe] = useState<LngLat | null>(null);
  const [apiMap, setApiMap] = useState('');
  const [nivelTrafico, setNivelTrafico] = useState(3)
  const [activePopup, _setActivePopup] = useState<maplibregl.Popup | null>(null);
  const activePopupRef = useRef(activePopup);
  const [showSenal, _setShowSenal] = useState(true)
  const showSenalRef = useRef(showSenal);
  const [showPanels, _setShowPanels] = useState(true)
  const showPanelsRef = useRef(showPanels);
  const setActivePopup = (data: maplibregl.Popup | null) => {
    activePopupRef.current = data;
    _setActivePopup(data);
  };
  const setShowPanels = (data: boolean) => {
    showPanelsRef.current = data;
    _setShowPanels(data);
  };
  const setShowSenal = (data: boolean) => {
    showSenalRef.current = data;
    _setShowSenal(data);
  };


  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await fetch('/config.json');
        const config = await response.json();
        setApiUrlConfig(config.API_URL)
        setApiMap(config.API_MAPA)
        const { MAP_CENTER, MAP_BOUNDS } = config;
        setLng(MAP_CENTER.lng);
        setLat(MAP_CENTER.lat);
        const southWest = new LngLat(MAP_BOUNDS.sw.lng, MAP_BOUNDS.sw.lat);
        const northEast = new LngLat(MAP_BOUNDS.ne.lng, MAP_BOUNDS.ne.lat);

        setSw(southWest);
        setNe(northEast);
        setNivelTrafico(config.NIVEL_TRAFICO)
        setConfigLoaded(true)
      } catch (error) {
        console.error('Error loading config:', error);
      }
    };

    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const map = new maplibregl.Map({
      container: 'map',
      style: apiMap,
      center: [lng ?? 0, lat ?? 0],
      zoom: width > 1300 ? isPrincipal ? 12.4 : 11 : isPrincipal ? 10 : 11,
      maxBounds: new LngLatBounds(sw ? sw : [0, 0], ne ? ne : [0, 0]),
      minZoom: width > 1300 ? isPrincipal ? 12.4 : 11 : isPrincipal ? 10 : 11,
      maxZoom: 18,
      attributionControl: false
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

    map.on('load', async () => {
      const imageCamara = await map.loadImage('/FCAM_L.png');
      const camaraCluster = await map.loadImage('/camara-cluster.png');
      const imageParking = await map.loadImage('/azul.png');
      const parkingCluster = await map.loadImage('/parking-cluster.png');
      const parkingClusterRed = await map.loadImage('/parking-cluster-red.png');
      const imageParkingRojo = await map.loadImage('/rojo.png');
      const imageParkingAmarillo = await map.loadImage('/amarillo.png');
      const imagePrivado = await map.loadImage('/privado.png');
      const imageIncidencia1 = await map.loadImage('/incidencia1.png');
      const imageIncidencia2 = await map.loadImage('/incidencia2.png');
      const imageIncidencia3 = await map.loadImage('/incidencia3.png');
      const pilona_abierta = await map.loadImage('/abierto.png');
      const pilona_cerrada = await map.loadImage('/cerrado.png');
      const pilona_nocomunica = await map.loadImage('/no_comunica.png');
      const pilona_carga_descarga = await map.loadImage('/carga_descarga.png');
      const pilona_normal = await map.loadImage('/normal.png');
      const cluster_pilona = await map.loadImage('/cluster-pilona.png');
      map.addImage('camara', imageCamara.data);
      map.addImage('camara-cluster', camaraCluster.data);
      map.addImage('parking_N', imageParking.data);
      map.addImage('parking-cluster', parkingCluster.data);
      map.addImage('parking-cluster-red', parkingClusterRed.data);
      map.addImage('parking_A', imageParkingAmarillo.data);
      map.addImage('parking_R', imageParkingRojo.data);
      map.addImage('privado', imagePrivado.data);
      map.addImage('incidencia1', imageIncidencia1.data);
      map.addImage('incidencia2', imageIncidencia2.data);
      map.addImage('incidencia3', imageIncidencia3.data);
      map.addImage('pilona_abierta', pilona_abierta.data);
      map.addImage('pilona_cerrada', pilona_cerrada.data);
      map.addImage('pilona_nocomunica', pilona_nocomunica.data);
      map.addImage('pilona_carga_descarga', pilona_carga_descarga.data);
      map.addImage('pilona_normal', pilona_normal.data);
      map.addImage('cluster_pilona', cluster_pilona.data);

      addPanelMarkers()
      addSenalMarkers()
      if (info) {
        map.addSource('tramos', {
          'type': 'geojson',
          'data': {
            "type": "FeatureCollection",
            "features": info.map(ele => {
              return {
                "type": "Feature",
                "properties": {
                  "description": ele.DESCRIPCION,
                  "carga": ele.CARGA,
                  "ancho_linea": 4,
                  "color_linea": obtenerEstadoTrafico(ele.CARGA, nivelTrafico).color
                },
                "geometry": {
                  "type": "LineString",
                  "coordinates": ele.TRAMOS_POSICIONES.map(coord => {
                    return [coord.LONGITUD, coord.LATITUD]
                  })
                }
              }
            })
          }
        });

        map.addLayer({
          'id': 'tramos',
          'type': 'line',
          'source': 'tramos',
          'layout': {
            'line-join': 'round',
            'line-cap': 'round'
          },
          'paint': {
            'line-color': ['get', 'color_linea'],
            'line-width': ['get', 'ancho_linea']
          }
        });
      }

      addGeoJsonSource(map, 'camaras',
        coordinates?.filter(ele => ele.nombre === undefined && ele.fechaInicio === undefined && ele.plazasLibres === undefined && ele.estado === undefined),
        0.25,
        true
      );

      addGeoJsonSource(map, 'aparcamientos',
        coordinates?.filter(ele => ele.plazasLibres !== undefined),
        0.07,
        true,
        {
          plazasLibresCount: ['+', ['case', ['==', ['get', 'plazasLibres'], 'LLENO'], 1, 0]]
        }
      );

      addGeoJsonSource(map, 'incidencias',
        coordinates?.filter(ele => ele.fechaInicio !== undefined),
        0.12,
        false
      );

      addGeoJsonSource(map, 'pilonas',
        coordinates?.filter(ele => ele.estado !== undefined && ele.desc_estado !== undefined),
        0.12,
        true
      );

      const popupContainer = document.createElement('div');
      popupContainer.className = 'popup-container';
      popupContainer.style.position = 'absolute';
      document.body.appendChild(popupContainer);

      addClusterLayer(map, 'clusters-camaras', 'camaras', 'camara-cluster', 0.3, [0, -10]);
      addClusterLayer(map, 'clusters-aparcamientos', 'aparcamientos', 'parking-cluster', 0.4, [20, 0], true);
      addClusterLayer(map, 'clusters-pilonas', 'pilonas', 'cluster_pilona', 0.12, null);
      addClusterCountLayer(map, 'cluster-count-pilonas', 'pilonas', [8, -8]);
      addClusterCountLayer(map, 'cluster-count-camaras', 'camaras', [10, -22]);
      addClusterCountLayer(map, 'cluster-count-aparcamientos', 'aparcamientos', [31, -16]);
      addUnclusteredPointLayer(map, 'unclustered-point-aparcamientos', 'aparcamientos');
      addUnclusteredPointLayer(map, 'unclustered-point-camaras', 'camaras');
      addUnclusteredPointLayer(map, 'unclustered-point-incidencias', 'incidencias');
      addUnclusteredPointLayer(map, 'unclustered-point-pilonas', 'pilonas');

      const showPopup = (coordinates: [number, number], content: string) => {
        if (!map) return;

        if (activePopup) {
          activePopup.remove();
        }

        const popup = new maplibregl.Popup()
          .setLngLat(coordinates)
          .setHTML(content)
          .addTo(map);

        setActivePopup(popup);

        popup.on('close', () => {
          setActivePopup(null);
        });
      };

      const layers = ['unclustered-point-aparcamientos', 'unclustered-point-camaras', 'unclustered-point-incidencias', 'unclustered-point-pilonas']
      const layersClusters = ['clusters-camaras', 'clusters-aparcamientos', 'clusters-pilonas']

      layersClusters.forEach(layer => {
        map.on('click', layer, (e) => {
          mapClickCluster(e, map, layer.substring(9))
        });
      })

      layers.forEach(layer => {
        map.on('click', layer, async (e) => {
          let active = activePopupRef.current
          mapClick(e, map, coordinates, apiUrlConfig, instance, showPopup, active?.getLngLat())
        })
        map.on('mouseenter', layer, (e) => {
          let active = activePopupRef.current
          mouseEnter(e, map, coordinates, width, popupContainer, active?.getLngLat())
        });
        map.on('mouseleave', layer, () => {
          map.getCanvas().style.cursor = '';
          popupContainer.style.display = 'none';
        });
      }
      )

      map.on('mousemove', (e) => {
        popupContainer.style.left = e.originalEvent.clientX + 10 + 'px';
        popupContainer.style.top = e.originalEvent.clientY + 'px';
      });

      if (info && width > 1300) {
        const layerMenu = document.createElement('div');
        layerMenu.className = 'layer-menu';

        const layers = ['Tramos', 'Aparcamientos', 'Incidencias', 'Camaras', 'Pilonas', 'Paneles', 'Señales'];
        layers.forEach(layer => {

          const input = document.createElement('input');
          input.type = 'checkbox';

          input.id = layer.toLowerCase();
          input.name = layer;
          input.checked = true;

          const label = document.createElement('label');
          label.htmlFor = layer.toLowerCase();
          label.innerText = layer;

          input.onclick = () => {
            const layerId = layer.toLowerCase();
            if (layerId === 'paneles') {
              if (input.checked) {
                setShowPanels(true)
                addPanelMarkers();
              } else {
                setShowPanels(false)
                removePanelMarkers();
              }
            } else if (layerId === 'señales') {
              if (input.checked) {
                setShowSenal(true)
                addSenalMarkers();
              } else {
                setShowSenal(false)
                removeSenalMarkers();
              }
            } else {
              const newLayerId = layerId === 'tramos' ? layerId : `unclustered-point-${layerId}`;
              const visibility = map.getLayoutProperty(newLayerId, 'visibility')
              if (visibility === 'visible' || visibility === undefined) {
                if (layerId === 'tramos') {
                  map.setLayoutProperty('tramos', 'visibility', 'none');
                } else {
                  map.setLayoutProperty(`clusters-${layerId}`, 'visibility', 'none');
                  map.setLayoutProperty(`unclustered-point-${layerId}`, 'visibility', 'none');
                  map.setLayoutProperty(`cluster-count-${layerId}`, 'visibility', 'none');
                }
              } else {
                if (layerId === 'tramos') {
                  map.setLayoutProperty(layerId, 'visibility', 'visible');
                } else {
                  map.setLayoutProperty(`clusters-${layerId}`, 'visibility', 'visible');
                  map.setLayoutProperty(`unclustered-point-${layerId}`, 'visibility', 'visible');
                  map.setLayoutProperty(`cluster-count-${layerId}`, 'visibility', 'visible');
                }
              }
            };
          }
          layerMenu.appendChild(input);
          layerMenu.appendChild(label);
          layerMenu.appendChild(document.createElement('br'));

        });
        map.getContainer().appendChild(layerMenu);
      }

      if (info && width > 1300) {
        const legendContainer = document.createElement('div');
        legendContainer.className = 'legend-container';
        legendContainer.innerHTML = ReactDOMServer.renderToString(
          <Leyenda />
        )
        const legendContainer2 = document.createElement('div');
        legendContainer2.className = 'legend-container2';
        legendContainer2.innerHTML = ReactDOMServer.renderToString(
          <h3 style={{ marginTop: '10px', display: 'flex', alignItems: 'center' }}>Leyenda<div style={{ width: '30px', height: '30px', marginLeft: '40px' }}><KeyboardArrowDownIcon /></div></h3>
        )
        legendContainer2.onclick = () => {
          legendContainer.style.display = 'block'
          legendContainer2.style.display = 'none'
        }
        legendContainer.onclick = () => {
          legendContainer2.style.display = 'block'
          legendContainer.style.display = 'none'
        }
        map.getContainer().appendChild(legendContainer);
        map.getContainer().appendChild(legendContainer2);
      }

      function addPanelMarkers() {
        if (showPanelsRef.current) {
          coordinates?.filter(ele => ele.nombre !== undefined && ele.isPanel).forEach((ele) => {
            const el = document.createElement('img');
            el.className = 'marker';

            const zoom = mapRef.current?.getZoom();
            el.src = zoom && zoom > 14 ? ele.image === '' || ele.image === undefined || ele.estado === 1 || ele.forzadura === 1 ? '/panel.png' : ele.image : '/panel.png'
            el.style.width = zoom && zoom > 14 ? '80px' : '50px';
            el.style.height = zoom && zoom > 14 ? '60px' : '50px';
            if (zoom && zoom > 14 && ele.estado !== 1 && ele.forzadura !== 1 && ele.image !== '' && ele.image !== undefined) {
              el.addEventListener('mouseenter', () => {
                el.style.width = '160px';
                el.style.height = '105px';
              });

              el.addEventListener('mouseleave', () => {
                el.style.width = '80px';
                el.style.height = '60px';
              });
            }

            el.addEventListener('mouseenter', () => {
              mouseEnterMarker(map, ele.nombre, ele.isPanel, ele.estado, width, popupContainer)
            });

            el.addEventListener('mouseleave', () => {
              popupContainer.style.display = 'none';
              map.getCanvas().style.cursor = '';
            });

            if (mapRef.current) {
              const marker = new maplibregl.Marker({ element: el })
                .setLngLat([ele.lng ?? 0, ele.lat ?? 0])
                .addTo(mapRef.current);
              markersPanels.current.push(marker)
              marker.getElement().addEventListener('click', () => {
                const currentZoom = mapRef.current?.getZoom();
                if (currentZoom ?? 0 < 14) {
                  mapRef.current?.flyTo({
                    center: [ele.lng ?? 0, ele.lat ?? 0],
                    zoom: 16,
                    essential: true
                  });
                }
              });
            }
          });
        }

      }

      function removePanelMarkers() {
        markersPanels.current.forEach(marker => marker.remove());
        markersPanels.current = [];
      }

      function addSenalMarkers() {
        if (showSenalRef.current) {

          coordinates?.filter(ele => ele.nombre !== undefined && !ele.isPanel).forEach((ele) => {
            const el = document.createElement('img');
            el.className = 'marker';

            const zoom = mapRef.current?.getZoom();
            el.src = zoom && zoom > 14 ? ele.image === '' || ele.image === undefined || ele.estado === -1 ? '/senal.png' : ele.image : '/senal.png'
            el.style.width = zoom && zoom > 14 ? '50px' : '40px';
            el.style.height = zoom && zoom > 14 ? '50px' : '40px';
            if (zoom && zoom > 14 && ele.estado !== -1 && ele.image !== '' && ele.image !== undefined) {
              el.addEventListener('mouseenter', () => {
                el.style.width = '240px';
                el.style.height = '240px';
                el.style.zIndex = '5'
              });

              el.addEventListener('mouseleave', () => {
                el.style.width = '50px';
                el.style.height = '50px';
                el.style.zIndex = '0'
              });
            }

            el.addEventListener('mouseenter', () => {
              mouseEnterMarker(map, ele.nombre, ele.isPanel, ele.estado, width, popupContainer)
            });

            el.addEventListener('mouseleave', () => {
              popupContainer.style.display = 'none';
              map.getCanvas().style.cursor = '';
            });

            if (mapRef.current) {
              const marker = new maplibregl.Marker({ element: el })
                .setLngLat([ele.lng ?? 0, ele.lat ?? 0])
                .addTo(mapRef.current);
              markersSenals.current.push(marker)
              marker.getElement().addEventListener('click', () => {
                const currentZoom = mapRef.current?.getZoom();
                if (currentZoom ?? 0 < 14) {
                  mapRef.current?.flyTo({
                    center: [ele.lng ?? 0, ele.lat ?? 0],
                    zoom: 16,
                    essential: true
                  });
                }
              });
            }
          });
        }
      }


      map.on('drag', () => {
        popupContainer.style.display = 'none';
      });

      // Función para remover los marcadores de paneles
      function removeSenalMarkers() {
        markersSenals.current.forEach(marker => marker.remove());
        markersSenals.current = [];
      }

      const updateMarkers = () => {
        removePanelMarkers()
        addPanelMarkers()
        removeSenalMarkers()
        addSenalMarkers()
      };

      // Escucha el evento de cambio de zoom
      if (mapRef.current) {
        mapRef.current.on('zoom', updateMarkers);
      }

      updateMarkers();

    });
    return () => {
      map.remove()
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinates, configLoaded, apiUrlConfig, info]);

  useEffect(() => {
    if (coordinatesToGo && mapRef.current) {
      if (coordinatesToGo.lng > -4.13 && coordinatesToGo.lng < -3.92) {
        mapRef.current.flyTo({
          center: [coordinatesToGo.lng, coordinatesToGo.lat],
          zoom: 18,
          essential: true
        });
      }
      if (setMarked) {
        setMarked('');
      }
    }
  }, [coordinatesToGo, setMarked]);

  return (
    <div>
      <div
        id="map"
        style={{
          height: width > 750 ? (height ? height : '767px') : width,
          width: mapWidth ? mapWidth : '100%',
          borderRadius: '5px'
        }}
      />
    </div>
  );
};

export default Mapa;